import { HStack, Text } from "@chakra-ui/react";
import { OutOfOfficeIcon } from "modules/icons";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useMemo } from "react";
import colors from "app/colors";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import {
  getDistributionGroupMention,
  isUserOutOfOffice,
} from "modules/utils/Functions";

interface IProps {
  draftId: string;
}

const OutOfOfficeNote = ({ draftId }: IProps) => {
  const { mentionIds } = useMessageThreadDraft({ draftId });
  const { getUsersById } = useUsers();
  const { getDistributionGroupsById } = useDistributionGroups();

  const users = useMemo(
    () => getUsersById(mentionIds),
    [getUsersById, mentionIds],
  );

  const outOfOfficeUsers = useMemo(
    () => users.filter((user) => isUserOutOfOffice(user)),
    [users],
  );

  const outOfOfficeUserNote = useMemo(() => {
    if (outOfOfficeUsers.length === 0) {
      return null;
    }
    const outOfOfficeUserNames = outOfOfficeUsers.map(
      (user) => user.firstName ?? user.lastName ?? user.callout,
    );
    const visibleNames = outOfOfficeUserNames.slice(0, 5);
    const remainingCount = outOfOfficeUserNames.length - visibleNames.length;

    let nameString = visibleNames.join(", ");

    if (visibleNames.length === 1) {
      return `${nameString} is away. `;
    }

    if (remainingCount > 0) {
      // Replace the last comma with ", and"
      const lastCommaIndex = nameString.lastIndexOf(",");
      if (lastCommaIndex !== -1) {
        nameString = `${nameString.substring(
          0,
          lastCommaIndex,
        )} and${nameString.substring(lastCommaIndex + 1)}`;
      }
      return `${nameString} and ${remainingCount} more people are away. `;
    }

    const lastCommaIndex = nameString.lastIndexOf(",");
    if (lastCommaIndex !== -1) {
      nameString = `${nameString.substring(
        0,
        lastCommaIndex,
      )} and${nameString.substring(lastCommaIndex + 1)}`;
    }
    return `${nameString} are away. `;
  }, [outOfOfficeUsers]);

  const distributionGroups = useMemo(
    () => getDistributionGroupsById(mentionIds),
    [getDistributionGroupsById, mentionIds],
  );

  const outOfOfficeDistributionGroups = useMemo(
    () =>
      distributionGroups
        .map((distributionGroup) => {
          const users = getUsersById(distributionGroup.userIdList);
          const outOfOfficeUsers = users.filter((user) =>
            isUserOutOfOffice(user),
          );
          return {
            name: distributionGroup.name,
            count: outOfOfficeUsers.length,
          };
        })
        .filter((group) => group.count > 0),
    [distributionGroups, getUsersById],
  );

  const outOfOfficeDistributionGroupNote = useMemo(() => {
    if (outOfOfficeDistributionGroups.length === 0) {
      return null;
    }

    const distributionGroupStrings = outOfOfficeDistributionGroups.map(
      (distributionGroup) => {
        const count = distributionGroup.count;
        const name = getDistributionGroupMention(distributionGroup);
        return `${count} member${count === 1 ? "" : "s"} of ${name}`;
      },
    );

    if (distributionGroupStrings.length === 1) {
      return `${distributionGroupStrings[0]} ${outOfOfficeDistributionGroups[0].count === 1 ? "is" : "are"} ${outOfOfficeUsers.length > 0 ? "also" : ""} away.`;
    }

    const lastGroup = distributionGroupStrings.pop();
    return `${distributionGroupStrings.join(", ")} and ${lastGroup} are ${outOfOfficeUsers.length > 0 ? "also" : ""} away.`;
  }, [outOfOfficeDistributionGroups, outOfOfficeUsers.length]);

  if (!outOfOfficeUserNote && !outOfOfficeDistributionGroupNote) {
    return null;
  }

  return (
    <HStack bg={colors.yellow20} p={1} borderRadius={4} w="full" spacing={1}>
      <OutOfOfficeIcon fill={colors.yellowDark} boxSize={4} />
      <Text color={colors.gray50} fontWeight={600} fontSize={12}>
        {outOfOfficeUserNote}
        {outOfOfficeDistributionGroupNote}
      </Text>
    </HStack>
  );
};

export default memo(OutOfOfficeNote);
